import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Save, User, Youtube, Facebook, Music, Upload, Check, AlertCircle, Gift, Loader2, Camera } from 'lucide-react';

function EditProfile() {
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    stage_name: '',
    picture: '',
    talents: [],
    performance_description: '',
    social_media_description: ''
  });
  const [socialLinks, setSocialLinks] = useState({
    youtube: '',
    facebook: '',
    spotify: ''
  });
  const [talentsInput, setTalentsInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [validatingLink, setValidatingLink] = useState(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [rewardsClaimed, setRewardsClaimed] = useState({});
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUser();
  }, []);

  const fetchUser = async () => {
    try {
      const response = await fetch(`${API}/auth/me`, { credentials: 'include' });
      const data = await response.json();
      
      if (!response.ok) {
        toast.error('Please login first');
        navigate('/login');
        return;
      }
      
      setUser(data);
      setFormData({
        name: data.name || '',
        stage_name: data.stage_name || '',
        picture: data.picture || '',
        talents: data.talents || [],
        performance_description: data.performance_description || '',
        social_media_description: data.social_media_description || ''
      });
      setSocialLinks({
        youtube: data.youtube_link || '',
        facebook: data.facebook_link || '',
        spotify: data.spotify_link || ''
      });
      setTalentsInput((data.talents || []).join(', '));
      setRewardsClaimed(data.rewards_claimed || {});
    } catch (error) {
      toast.error('Failed to load profile');
      navigate('/login');
    }
  };

  const handleValidateSocialLink = async (linkType) => {
    const linkUrl = socialLinks[linkType];
    
    if (!linkUrl.trim()) {
      toast.error(`Please enter a ${linkType.charAt(0).toUpperCase() + linkType.slice(1)} link`);
      return;
    }

    setValidatingLink(linkType);
    
    try {
      const response = await fetch(`${API}/users/validate-social-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          link_type: linkType,
          link_url: linkUrl
        })
      });

      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error('Invalid response from server');
      }

      if (!response.ok) {
        throw new Error(data.detail || 'Validation failed');
      }

      if (data.votes_awarded > 0) {
        toast.success(
          <div className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-secondary" />
            <span>You received <strong>{data.votes_awarded} votes!</strong></span>
          </div>
        );
        setRewardsClaimed(prev => ({ ...prev, [linkType]: true }));
        setUser(prev => ({ ...prev, free_votes_remaining: data.free_votes_remaining }));
      } else {
        toast.success(`${linkType.charAt(0).toUpperCase() + linkType.slice(1)} link validated!`);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setValidatingLink(null);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Check file size (10MB limit)
    const MAX_SIZE = 10 * 1024 * 1024; // 10MB
    if (file.size > MAX_SIZE) {
      toast.error(`Image size exceeds 10MB limit. Your image is ${(file.size / (1024 * 1024)).toFixed(1)}MB`);
      return;
    }

    setUploadingPicture(true);

    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64Data = event.target.result;
        
        try {
          const response = await fetch(`${API}/users/upload-profile-picture`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              image_data: base64Data,
              content_type: file.type
            })
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.detail || 'Upload failed');
          }

          // Update the form with the new picture
          setFormData(prev => ({ ...prev, picture: data.picture_url }));
          
          if (data.votes_awarded > 0) {
            toast.success(
              <div className="flex items-center gap-2">
                <Gift className="h-5 w-5 text-secondary" />
                <span>Profile picture uploaded! You received <strong>{data.votes_awarded} votes!</strong></span>
              </div>
            );
            setRewardsClaimed(prev => ({ ...prev, profile_picture: true }));
            setUser(prev => ({ ...prev, free_votes_remaining: data.free_votes_remaining }));
          } else {
            toast.success('Profile picture uploaded successfully!');
          }
        } catch (error) {
          toast.error(error.message);
        } finally {
          setUploadingPicture(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to read image file');
      setUploadingPicture(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const talentsArray = talentsInput.split(',').map(t => t.trim()).filter(t => t);
      
      const updateData = {
        ...formData,
        talents: talentsArray
      };

      // Update user profile
      const response = await fetch(`${API}/users/${user.user_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updateData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to update profile');
      }

      toast.success('Profile updated successfully!');
      navigate(`/profile/${user.user_id}`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSocialLinkChange = (type, value) => {
    setSocialLinks({ ...socialLinks, [type]: value });
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const SocialLinkField = ({ type, icon: Icon, label, placeholder, color }) => {
    const isValidating = validatingLink === type;
    const isClaimed = rewardsClaimed[type];
    
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium flex items-center gap-2">
            <Icon className={`h-4 w-4 ${color}`} />
            {label}
          </label>
          {isClaimed && (
            <span className="text-xs text-secondary flex items-center gap-1">
              <Check className="h-3 w-3" /> +5 votes claimed
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <Input
            type="url"
            value={socialLinks[type]}
            onChange={(e) => handleSocialLinkChange(type, e.target.value)}
            className="flex-1 bg-white/5 border-white/10 focus:border-primary h-12 rounded-xl"
            placeholder={placeholder}
            data-testid={`${type}-link-input`}
          />
          <Button
            type="button"
            onClick={() => handleValidateSocialLink(type)}
            disabled={isValidating || !socialLinks[type]}
            className={`rounded-xl h-12 px-4 ${isClaimed ? 'bg-secondary/20 text-secondary' : 'bg-primary'}`}
            data-testid={`validate-${type}-btn`}
          >
            {isValidating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : isClaimed ? (
              <Check className="h-4 w-4" />
            ) : (
              'Validate'
            )}
          </Button>
        </div>
        {!isClaimed && (
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <Gift className="h-3 w-3 text-secondary" />
            Add a valid link to earn +5 votes
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen pb-24">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4" data-testid="back-btn">
          ← Back
        </Button>

        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-2 tracking-tight">Edit Profile</h1>
          <p className="text-muted-foreground">Update your information</p>
          {user && (
            <p className="text-sm mt-2">
              Your vote balance: <span className="text-secondary font-bold">{user.free_votes_remaining || 0}</span> votes
            </p>
          )}
        </div>

        {/* Profile Picture Upload Card */}
        <Card className="glass-panel p-6 mb-6">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <Camera className="h-5 w-5 text-primary" />
            Profile Picture
            {rewardsClaimed.profile_picture && (
              <span className="text-xs bg-secondary/20 text-secondary px-2 py-1 rounded-full ml-auto">
                +5 votes claimed
              </span>
            )}
          </h3>
          
          <div className="flex flex-col md:flex-row items-center gap-6">
            {/* Preview */}
            <div className="w-32 h-32 rounded-full bg-white/10 flex items-center justify-center overflow-hidden border-2 border-dashed border-white/20">
              {formData.picture ? (
                <img 
                  src={formData.picture} 
                  alt="Profile" 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div className={`flex flex-col items-center justify-center ${formData.picture ? 'hidden' : ''}`}>
                <User className="h-12 w-12 text-muted-foreground" />
              </div>
            </div>
            
            {/* Upload Button */}
            <div className="flex-1 space-y-3">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="image/*"
                className="hidden"
                data-testid="picture-file-input"
              />
              <Button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPicture}
                className="w-full md:w-auto bg-primary rounded-xl h-12"
                data-testid="upload-picture-btn"
              >
                {uploadingPicture ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Upload Picture
                  </>
                )}
              </Button>
              <p className="text-xs text-muted-foreground">
                Max file size: 10MB. Supported formats: JPG, PNG, GIF, WebP
              </p>
              {!rewardsClaimed.profile_picture && (
                <p className="text-xs text-secondary flex items-center gap-1">
                  <Gift className="h-3 w-3" />
                  Upload a profile picture to earn +5 votes
                </p>
              )}
            </div>
          </div>
        </Card>

        {/* Social Media Links Card */}
        <Card className="glass-panel p-6 mb-6">
          <h3 className="text-lg font-bold mb-4">Social Media Links</h3>
          <p className="text-sm text-muted-foreground mb-6">
            Add your social media links to earn votes and help fans connect with you
          </p>
          
          <div className="space-y-6">
            <SocialLinkField
              type="youtube"
              icon={Youtube}
              label="YouTube Link"
              placeholder="https://youtube.com/@yourchannel"
              color="text-red-500"
            />
            
            <SocialLinkField
              type="facebook"
              icon={Facebook}
              label="Facebook Link"
              placeholder="https://facebook.com/yourpage"
              color="text-blue-500"
            />
            
            <SocialLinkField
              type="spotify"
              icon={Music}
              label="Spotify Link"
              placeholder="https://open.spotify.com/artist/..."
              color="text-green-500"
            />
          </div>
        </Card>

        {/* Main Profile Form */}
        <Card className="glass-panel p-6 md:p-8">
          <h3 className="text-lg font-bold mb-6">Profile Information</h3>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="text-sm font-medium mb-2 block">Name</label>
              <Input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="bg-white/5 border-white/10 focus:border-primary h-12 rounded-xl"
                required
                data-testid="edit-name-input"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Stage Name</label>
              <Input
                type="text"
                name="stage_name"
                value={formData.stage_name}
                onChange={handleChange}
                className="bg-white/5 border-white/10 focus:border-primary h-12 rounded-xl"
                data-testid="edit-stage-name-input"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Talents (comma-separated)</label>
              <Input
                type="text"
                value={talentsInput}
                onChange={(e) => setTalentsInput(e.target.value)}
                className="bg-white/5 border-white/10 focus:border-primary h-12 rounded-xl"
                placeholder="Singing, Dancing, Acting"
                data-testid="edit-talents-input"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Performance Description</label>
              <Textarea
                name="performance_description"
                value={formData.performance_description}
                onChange={handleChange}
                className="bg-white/5 border-white/10 focus:border-primary rounded-xl min-h-[120px]"
                placeholder="Describe your performance style..."
                data-testid="edit-performance-input"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Social Media Description</label>
              <Textarea
                name="social_media_description"
                value={formData.social_media_description}
                onChange={handleChange}
                className="bg-white/5 border-white/10 focus:border-primary rounded-xl min-h-[120px]"
                placeholder="Your social media presence..."
                data-testid="edit-social-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-primary hover:scale-105 transition-transform rounded-full py-6 font-bold"
              disabled={isLoading}
              data-testid="save-profile-btn"
            >
              <Save className="mr-2 h-5 w-5" />
              {isLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}

export default EditProfile;
